
import { uploadFiles, copyItem, renameItem, downloadFile, downloadBatch, createDirectory } from '../api/fs.js';
import { apiUpload, apiDownloadFromUrl, apiListFromUrl } from '../api/client.js';
import { showToast, showUploadToast, showDownloadToast, showConfirmDialog } from './ui.js';
import { getUploadSizeLimit, getActiveMachine, getMachineBaseURL, BASE_PATH } from './config.js';
import { getToken } from './storage.js';

function triggerBlobDownload(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

/**
 * Download files or folders
 * For single item: direct stream body chunk read with progress toast
 * For multiple items: batch zip stream body chunk read with indeterminate progress toast
 * @param {Array<string>} selectedPaths - Array of full paths to download
 */
export async function downloadItems(selectedPaths) {
    if (!selectedPaths || selectedPaths.length === 0) {
        showToast('No items selected for download', 'warning');
        return;
    }
    
    const toast = showDownloadToast();
    
    try {
        if (selectedPaths.length === 1) {
            const path = selectedPaths[0];
            const name = decodeURIComponent(path.split('/').pop() || 'download');
            const blob = await downloadFile(path, (percent, metrics) => {
                toast.update(percent, metrics);
            });
            triggerBlobDownload(blob, name);
            toast.finish(true, 'Download complete!');
        } else {
            const blob = await downloadBatch(selectedPaths, (percent, metrics) => {
                toast.update(percent, metrics);
            });
            triggerBlobDownload(blob, 'download.zip');
            toast.finish(true, 'Download complete!');
        }
    } catch (err) {
        console.error('Download error:', err);
        toast.finish(false, `Download failed: ${err.message}`);
    }
}

/**
 * Copy clipboard items to destination
 * @param {string} destinationPath - Target directory path
 * @returns {Promise<boolean>} Success status
 */
export async function pasteItems(destinationPath) {
    const clipboard = window.appState.clipboard;
    if (!clipboard || clipboard.paths.length === 0) {
        showToast('Nothing to paste', 'warning');
        return false;
    }

    const activeMachine = getActiveMachine();
    const activeName = activeMachine?.name || 'Self';
    const isCrossMachine = clipboard.machine && clipboard.machine !== activeName;

    // Cut-paste to same directory is a no-op — clear clipboard silently
    if (!isCrossMachine && clipboard.operation === 'cut') {
        const allSamePath = clipboard.paths.every(item => {
            const fileName = item.path.split('/').pop();
            const destPath = destinationPath === '/' ? `/${fileName}` : `${destinationPath}/${fileName}`;
            return item.path === destPath;
        });
        if (allSamePath) {
            window.appState.clipboard = { operation: null, machine: null, machineUrl: null, paths: [] };
            window.dispatchEvent(new Event('clipboard-changed'));
            return true;
        }
    }

    try {
        showToast(`Pasting ${clipboard.paths.length} item(s)...`, 'info');

        if (isCrossMachine) {
            await _crossMachinePaste(clipboard, destinationPath);
        } else {
            await _sameMachinePaste(clipboard, destinationPath);
        }

        // Clear clipboard after paste
        window.appState.clipboard = { operation: null, machine: null, machineUrl: null, paths: [] };
        window.dispatchEvent(new Event('clipboard-changed'));

        showToast('Paste complete', 'success');
        return true;
    } catch (err) {
        console.error('Paste error:', err);
        showToast(`Paste failed: ${err.message}`, 'error');
        return false;
    }
}

/**
 * Same-machine paste: copyItem / renameItem (server-side).
 */
async function _sameMachinePaste(clipboard, destinationPath) {
    for (const item of clipboard.paths) {
        const sourcePath = item.path; // {path, isDir} schema
        const fileName = sourcePath.split('/').pop();
        let destPath = destinationPath === '/' ? `/${fileName}` : `${destinationPath}/${fileName}`;

        if (sourcePath === destPath) {
            if (clipboard.operation === 'cut') {
                continue;
            }
            destPath = _makeRenamePath(destPath);
        }

        if (clipboard.operation === 'copy') {
            await copyItem(sourcePath, destPath);
        } else if (clipboard.operation === 'cut') {
            await renameItem(sourcePath, destPath);
        }
    }
}

/**
 * Cross-machine paste: parallel pipeline (10 download / 10 upload, independent).
 *
 * Type detection: clipboard paths carry {path, isDir} from the UI's file list —
 * no server round-trip needed. Folders are recursively listed, downloaded, and
 * uploaded individually.
 *
 * Cut: after all transfers succeed, user is prompted to confirm deletion from source.
 * Cross-machine deletion is dangerous (no undo), hence the explicit confirm step.
 * Individual removal failures are reported but do not fail the overall paste.
 */
async function _crossMachinePaste(clipboard, destinationPath) {
    const srcMachine = clipboard.machine;
    const srcUrl = clipboard.machineUrl;
    const srcToken = getToken(srcMachine);

    if (!srcToken) {
        throw new Error(`Not authenticated on ${srcMachine}. Please log in to that machine first.`);
    }

    const toastLabel = clipboard.operation === 'cut' ? 'Moving' : 'Copying';
    let sourcePaths = [];

    // Flatten: expand folders into individual files, keep files as-is
    const queue = [];
    for (const item of clipboard.paths) {
        if (item.isDir) {
            sourcePaths.push(item.path);
            const files = await _listFolderRecursive(srcUrl, srcToken, item.path, destinationPath);
            for (const f of files) queue.push(f);
        } else {
            sourcePaths.push(item.path);
            queue.push({ srcPath: item.path, name: item.path.split('/').pop(), destDir: destinationPath });
        }
    }

    const total = queue.length;
    let done = 0;

    // Unified progress toast
    const toast = showUploadToast();
    try {
        toast.message(`${toastLabel} 0/${total} files from ${srcMachine}...`);

        await _transferPipeline(queue, srcUrl, srcToken, (count) => {
            done = count;
            toast.message(`${toastLabel} ${done}/${total} files from ${srcMachine}...`);
        });

        toast.finish(true, `${toastLabel} ${total} file(s) from ${srcMachine}`);
    } catch (err) {
        toast.finish(false, `Failed: ${err.message}`);
        throw err;
    }

    // Cut: cross-machine deletion is dangerous — ask user before deleting source.
    if (clipboard.operation === 'cut' && sourcePaths.length > 0) {
        const confirmed = await showConfirmDialog(
            'Delete from source?',
            `Delete ${sourcePaths.length} item(s) from ${srcMachine} after successful transfer?`
        );
        if (confirmed) {
            for (const p of sourcePaths) {
                try {
                    await _removeFromMachine(srcUrl, srcToken, p);
                } catch (err) {
                    showToast(`Failed to remove ${p} from ${srcMachine}: ${err.message}`, 'error');
                }
            }
        }
    }
}

/* ---- Semaphore + Pipeline (shared helpers) ---- */

class Semaphore {
    constructor(n) { this.n = n; this.waiting = []; }
    async acquire() {
        if (this.n > 0) { this.n--; return; }
        await new Promise(r => this.waiting.push(r));
    }
    release() { this.n++; const r = this.waiting.shift(); if (r) { this.n--; r(); } }
}

/**
 * Parallel pipeline: up to 10 concurrent downloads + 10 concurrent uploads.
 * Downloads and uploads run independently (not paired).
 * @param {Array} queue - [{srcPath, name, destDir}]
 * @param {string} srcUrl - source machine base URL
 * @param {string} srcToken - source machine auth token
 * @param {Function} onProgress - called with current count after each file completes
 */
async function _transferPipeline(queue, srcUrl, srcToken, onProgress) {
    const DL = new Semaphore(10);
    const UL = new Semaphore(10);
    let done = 0;

    await Promise.all(queue.map(async (entry) => {
        await DL.acquire();
        let blob;
        try {
            const result = await apiDownloadFromUrl(srcUrl, srcToken, entry.srcPath);
            blob = result.blob;
        } finally {
            DL.release();
        }

        await UL.acquire();
        try {
            await apiUpload(entry.destDir, [{ file: new File([blob], entry.name), name: entry.name }]);
        } finally {
            UL.release();
        }

        done++;
        onProgress(done);
    }));
}

/**
 * Recursively list a folder on a remote machine and return flat file entries.
 * @returns {Promise<Array<{srcPath: string, name: string, destDir: string}>>}
 */
async function _listFolderRecursive(srcUrl, srcToken, sourceDir, destDir) {
    const dirName = sourceDir.split('/').pop();
    const destFolder = destDir === '/' ? `/${dirName}` : `${destDir}/${dirName}`;
    await createDirectory(destFolder);

    const entries = await apiListFromUrl(srcUrl, srcToken, sourceDir);
    const result = [];

    for (const entry of entries) {
        const srcPath = entry.path || `${sourceDir}/${entry.name}`;
        if (entry.is_dir) {
            result.push(...await _listFolderRecursive(srcUrl, srcToken, srcPath, destFolder));
        } else {
            result.push({ srcPath, name: entry.name, destDir: destFolder });
        }
    }

    return result;
}

/**
 * Delete a file on a remote machine (for cross-machine cut).
 */
async function _removeFromMachine(baseUrl, token, path) {
    const response = await fetch(`${baseUrl}/api/fs/remove`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ path }),
    });

    if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`${response.status} ${response.statusText}${text ? ': ' + text : ''}`);
    }
}

/**
 * Generate a renamed path with _copy suffix for same-path paste.
 * "/path/to/file.txt" → "/path/to/file_copy.txt"
 * "/path/to/folder"   → "/path/to/folder_copy"
 * @param {string} fullPath
 * @returns {string}
 */
function _makeRenamePath(fullPath) {
    const lastSlash = fullPath.lastIndexOf('/');
    const dir = fullPath.substring(0, lastSlash);
    const name = fullPath.substring(lastSlash + 1);
    const dotIndex = name.lastIndexOf('.');
    
    let newName;
    if (dotIndex > 0) {
        // Has extension: file.txt → file_copy.txt
        newName = name.substring(0, dotIndex) + '_copy' + name.substring(dotIndex);
    } else {
        // No extension (folder or extensionless file): folder → folder_copy
        newName = name + '_copy';
    }
    return dir ? `${dir}/${newName}` : `/${newName}`;
}

/**
 * Set clipboard for copy operation
 * @param {Array<{path: string, isDir: boolean}>} items - Items to copy
 */
export function setClipboardCopy(items) {
    const machine = getActiveMachine();
    window.appState.clipboard = {
        operation: 'copy',
        machine: machine?.name || 'Self',
        machineUrl: machine?.isSelf === true ? window.location.origin + BASE_PATH : getMachineBaseURL(machine),
        paths: items.map(i => ({ path: i.path, isDir: !!i.isDir })),
    };
    window.dispatchEvent(new Event('clipboard-changed'));
    showToast(`${items.length} item(s) copied to clipboard`, 'success');
}

/**
 * Set clipboard for cut operation
 * @param {Array<{path: string, isDir: boolean}>} items - Items to cut
 */
export function setClipboardCut(items) {
    const machine = getActiveMachine();
    window.appState.clipboard = {
        operation: 'cut',
        machine: machine?.name || 'Self',
        machineUrl: machine?.isSelf === true ? window.location.origin + BASE_PATH : getMachineBaseURL(machine),
        paths: items.map(i => ({ path: i.path, isDir: !!i.isDir })),
    };
    window.dispatchEvent(new Event('clipboard-changed'));
    showToast(`${items.length} item(s) cut to clipboard`, 'success');
}

/**
 * Clear clipboard and remove cut visual indicators
 */
export function clearClipboard() {
    window.appState.clipboard = { operation: null, machine: null, machineUrl: null, paths: [] };
    window.dispatchEvent(new Event('clipboard-changed'));
}

/**
 * Move items to a destination directory.
 * Uses /api/fs/rename which handles cross-directory moves.
 * @param {Array<string>} sourcePaths - Absolute paths to move
 * @param {string} destDir - Destination directory
 * @returns {Promise<boolean>} Success
 */
export async function moveItemsToDir(sourcePaths, destDir) {
    if (!sourcePaths || sourcePaths.length === 0 || !destDir) return false;
    try {
        const cleanDest = destDir === '/' ? '' : destDir.replace(/\/+$/, '');
        let moved = 0;
        for (const sourcePath of sourcePaths) {
            const fileName = sourcePath.split('/').pop();
            const destPath = cleanDest ? `${cleanDest}/${fileName}` : `/${fileName}`;
            if (sourcePath === destPath) continue;
            await renameItem(sourcePath, destPath);
            moved++;
        }
        if (moved === 0) return true; // silent — nothing to move (same dir)
        showToast(`Moved ${moved} item(s)`, 'success');
        return true;
    } catch (err) {
        console.error('Move error:', err);
        showToast(`Move failed: ${err.message}`, 'error');
        return false;
    }
}

/**
 * Handles dropped items (files/folders) with recursive traversal.
 * Uses v0.12 path-preserving upload — server auto-creates parent dirs.
 * @param {DataTransferItemList} items 
 * @param {string} currentPath 
 */
export async function handleDropItems(items, currentPath) {
    // 1. Get entries from items
    const entries = [];
    for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry ? items[i].webkitGetAsEntry() : null;
        if (entry) {
            entries.push(entry);
        }
    }

    if (entries.length === 0) return;

    const toast = showUploadToast();

    try {
        // 2. Traverse and collect files with relative paths
        const filesWithPaths = [];
        const cleanPath = currentPath === '/' ? '' : currentPath.replace(/\/$/, '');

        const traverse = async (entry, parentRelative) => {
            if (entry.isFile) {
                const file = await getFileFromEntry(entry);
                const relativePath = parentRelative ? `${parentRelative}/${entry.name}` : entry.name;
                filesWithPaths.push({ file, name: relativePath });
            } else if (entry.isDirectory) {
                const dirRelative = parentRelative ? `${parentRelative}/${entry.name}` : entry.name;
                const children = await readDirectoryEntries(entry);
                for (const child of children) {
                    await traverse(child, dirRelative);
                }
            }
        };

        for (const entry of entries) {
            await traverse(entry, '');
        }

        if (filesWithPaths.length === 0) {
            toast.finish(false, 'No files found to upload');
            return;
        }

        // 2b. Early size guard — reject before building FormData / XHR
        const limitMB = getUploadSizeLimit();
        const limitBytes = limitMB * 1024 * 1024;
        const totalSize = filesWithPaths.reduce((sum, f) => sum + (f.file?.size || 0), 0);
        if (totalSize > limitBytes) {
            toast.finish(false, `File too large (limit: ${limitMB >= 1024 ? (limitMB / 1024) + ' GB' : limitMB + ' MB'})`);
            return;
        }

        // 3. Single upload call — server creates parent dirs from filename paths
        const targetPath = cleanPath || '/';
        await uploadFiles(targetPath, filesWithPaths, (percent, metrics) => {
            toast.update(percent, metrics);
        });
        toast.finish(true, 'Upload complete!');
        
        // Notify to refresh
        window.dispatchEvent(new Event('file-browser-refresh'));

    } catch (err) {
        console.error(err);
        toast.finish(false, `Upload failed: ${err.message}`);
    }
}

/**
 * Convert FileSystemFileEntry to File object
 */
function getFileFromEntry(entry) {
    return new Promise((resolve, reject) => {
        entry.file(resolve, reject);
    });
}

/**
 * Read all entries from a directory reader (handles batching)
 */
function readDirectoryEntries(dirEntry) {
    return new Promise((resolve, reject) => {
        const reader = dirEntry.createReader();
        let allEntries = [];

        function read() {
            reader.readEntries((entries) => {
                if (entries.length === 0) {
                    resolve(allEntries);
                } else {
                    allEntries = allEntries.concat(entries);
                    read(); // Keep reading until empty
                }
            }, reject);
        }

        read();
    });
}
